<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import { OFFER_MAX_LENGTH, OFFER_MESSAGE_MODES } from '../dateTrigger';

const props = defineProps({
  // action_params da ação: [{ inbox_id, message_mode, content, template_name, template_params }].
  modelValue: {
    type: [Array, Object],
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const DEFAULT_PARAMS = {
  inbox_id: null,
  message_mode: OFFER_MESSAGE_MODES.TEXT,
  content: '',
  template_name: null,
  template_params: {},
};
const MESSAGE_MODE_OPTIONS = [
  { value: OFFER_MESSAGE_MODES.TEXT, i18nKey: 'TEXT' },
  { value: OFFER_MESSAGE_MODES.TEMPLATE, i18nKey: 'TEMPLATE' },
];

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
// Só modelos que podem ser enviados (aprovados), o mesmo filtro do seletor de modelos.
const getSendableTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const showTemplatesModal = ref(false);

const params = computed(() => {
  const value = Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue;
  return { ...DEFAULT_PARAMS, ...(value || {}) };
});

const updateParams = patch => {
  emit('update:modelValue', [{ ...params.value, ...patch }]);
};

const inboxOptions = computed(() =>
  (whatsappInboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

// Trocar de caixa descarta o modelo, que pertence à caixa anterior.
const inboxId = computed({
  get: () => params.value.inbox_id ?? '',
  set: value =>
    updateParams({ inbox_id: value, template_name: null, template_params: {} }),
});

const isTemplateMode = computed(
  () => params.value.message_mode === OFFER_MESSAGE_MODES.TEMPLATE
);

const hasTemplates = computed(
  () =>
    Boolean(params.value.inbox_id) &&
    getSendableTemplates.value(params.value.inbox_id).length > 0
);

const content = computed({
  get: () => params.value.content || '',
  set: value => updateParams({ content: value }),
});

const selectMode = mode => updateParams({ message_mode: mode });

// O corpo preenchido do modelo vira o content, que é o texto que aparece na conversa.
const onTemplateSelect = ({ templateParams, pendingMessageContent }) => {
  updateParams({
    template_name: templateParams.name,
    template_params: templateParams,
    content: pendingMessageContent,
  });
  showTemplatesModal.value = false;
};

const clearTemplate = () => {
  updateParams({ template_name: null, template_params: {}, content: '' });
};
</script>

<template>
  <div data-test="offer-action" class="flex flex-col gap-2 mt-2">
    <div class="flex flex-wrap items-center gap-2">
      <FilterSelect
        v-model="inboxId"
        data-test="offer-inbox"
        :options="inboxOptions"
        :label="
          params.inbox_id
            ? null
            : $t('AUTOMATION.DATE_TRIGGER.OFFER.INBOX_PLACEHOLDER')
        "
      />
      <NextButton
        v-for="mode in MESSAGE_MODE_OPTIONS"
        :key="mode.value"
        :data-test="`offer-mode-${mode.value}`"
        :label="$t(`AUTOMATION.DATE_TRIGGER.OFFER.MODES.${mode.i18nKey}`)"
        :color="params.message_mode === mode.value ? 'blue' : 'slate'"
        faded
        xs
        @click="selectMode(mode.value)"
      />
    </div>

    <TextArea
      v-if="!isTemplateMode"
      v-model="content"
      data-test="offer-content"
      :max-length="OFFER_MAX_LENGTH"
      :placeholder="$t('AUTOMATION.DATE_TRIGGER.OFFER.CONTENT_PLACEHOLDER')"
    />
    <template v-else>
      <div
        v-if="params.template_name"
        data-test="offer-template"
        class="flex items-center gap-2 text-xs text-n-slate-11"
      >
        <span>
          {{
            $t('AUTOMATION.ACTION.TEMPLATE_SELECTED', {
              name: params.template_name,
            })
          }}
        </span>
        <NextButton ghost xs slate icon="i-lucide-x" @click="clearTemplate" />
      </div>
      <div v-else-if="hasTemplates">
        <NextButton
          data-test="offer-template-select"
          ghost
          xs
          icon="i-lucide-zap"
          :label="$t('AUTOMATION.DATE_TRIGGER.OFFER.TEMPLATE_SELECT')"
          @click="showTemplatesModal = true"
        />
      </div>
      <p
        v-else
        data-test="offer-no-templates"
        class="mb-0 text-xs text-n-amber-11"
      >
        {{
          params.inbox_id
            ? $t('AUTOMATION.DATE_TRIGGER.OFFER.NO_TEMPLATES')
            : $t('AUTOMATION.DATE_TRIGGER.OFFER.SELECT_INBOX_FIRST')
        }}
      </p>
    </template>

    <p data-test="offer-hint" class="mb-0 text-xs text-n-slate-11">
      {{ $t('AUTOMATION.DATE_TRIGGER.OFFER.HINT') }}
    </p>

    <WhatsappTemplates
      v-if="hasTemplates"
      v-model:show="showTemplatesModal"
      :inbox-id="params.inbox_id"
      :send-button-label="$t('AUTOMATION.ACTION.TEMPLATE_USE')"
      @on-send="onTemplateSelect"
      @cancel="showTemplatesModal = false"
    />
  </div>
</template>
